
import { Component, Vue, Watch } from "vue-property-decorator";
import TablePageWrapper from "@/components/layout/TablePageWrapper.vue";
import AppTableRates from "@/components/offers/personal-rates/AppTableRates.vue";
import { getEmployeesPersonalRates, postEmployeesPersonalRates, cloneEmployeesPersonalRates } from "@/api/offers";
import { IPersonalRates } from "@/api/types/offers";
import { Affiliate } from "@/services/affiliates/Affiliate";
import FormPersonalRate from "@/components/offers/personal-rates/FormPersonalRate.vue";
import { IColumnFilter } from "@/types";
import { PersonalRateColumns, PersonalRateFilters } from "@/services/TablePage/personalRates";
import { downloadExcelFile, showServerError } from "@/utils";
import BaseButtonV2 from "@/components-UI/buttons/BaseButtonV2.vue";
import SvgCopy from "@/assets/icons/copy-2.svg";
import { Getter } from "vuex-class";
import BaseQuestionMarkTooltip from "@/components-UI/BaseQuestionMarkTooltip.vue";
import { NotificationMessage } from '@/utils/notification';
import { IEditedPersonalRate, waitPersonalRatesApplied } from "@/utils/personalRate/personalRateUpdate";
import { Prop } from "vue-property-decorator";

// POST правки только ставит задачи в очередь offers-service, суммы переписывает консьюмер:
// перечитанный сразу список показал бы старые. Ждём по текущей странице списка - один запрос на круг
const waitUpdatedPersonalRates = async (
    rates: IEditedPersonalRate[],
    signal: AbortSignal,
    loadPage: () => Promise<IPersonalRates[]>,
): Promise<void> => {
    try {
        const isApplied = await waitPersonalRatesApplied(rates, loadPage, { signal });
        if (!isApplied && !signal.aborted) {
            new NotificationMessage('Цены будут применены в ближайшее время', 'info');
        }
    } catch {
        // Цену не перечитать: список перечитается и покажет то, что отдаст сервер
    }
};

@Component({
    components: {
        QuestionMarkTooltip: BaseQuestionMarkTooltip,
        TablePageWrapper,
        BaseButtonV2,
        SvgCopy,
        NotificationMessage,
    },
})


export default class PersonalRates extends Vue {

    @Prop({ type: Object, default: null }) affiliate!: Affiliate | null;
    @Getter('GET_FILTERS_PARAMS_OBJECT') getFiltersParamsObject;

    columns: IColumnFilter[] = new PersonalRateColumns().columns;
    filters: PersonalRateFilters = new PersonalRateFilters();
    readonly TOOLTIP_COPY = 'Копирует цены мастера, в которых индивидуальный доход отличается от стандартного не более, чем на 4%. Также копирует доступы к тем офферам, по которым сформированы цены.';


    personalRatesTable = {
        component: AppTableRates,
        apiMethod: getEmployeesPersonalRates,
        updateItemsMethod: postEmployeesPersonalRates,
        waitUpdatedItems: waitUpdatedPersonalRates,
        updateItemsKey: 'pairs',
        additionalKey: 'need_notify',
        tableItemKey: 'personal_rates.id',
    };

    createPersonalRateForm = {
        component: FormPersonalRate,
    };
    defaultSort = {
        sort: '',
        direction: '',
    };

    get getTitle(): string {
        return this.$route.name === 'personal-rates' ? 'Индивидуальные цены' : '';
    }

    get showCopyPrices(): boolean {
        if (!this.affiliate) return false;
        return Number(this.affiliate.is_master) === 0;
    }

    get isMobile(): boolean {
        return this.$vuetify.breakpoint.width <= 768;
    }


    created(): void {
        this.changeName();
        this.defaultSort.sort = this.$route.query.sort as string || 'id';
        this.defaultSort.direction = this.$route.query.direction as string || 'DESC';
    }

    async mounted(): Promise<void> {
        await this.filters.getBufferAccountsItems();
    }

    @Watch('$vuetify.breakpoint.width')
    changeName(): void {
        const item = this.filters.filters.find(i => i.id === 'personal-rate');
        const text = this.$vuetify.breakpoint.width <= 440 ? 'Индивид.цена' : 'Индивидуальная цена';
        if (item) {
            this.$set(item, 'name', text);
        }
    }

    disabledDownload(): boolean {
        const requiredFilters = [
            'affiliate_id',
            'offer_legacy_id',
            'advertiser_id',
        ];
        return !Object.keys(this.getFiltersParamsObject).some((item) => requiredFilters.includes(item));
    }

    downloadOptions = {
        loading: false,
        tooltip: 'Примените фильтр по офферу, рекламодателю и/или партнёру',
        disabled: this.disabledDownload,
    };

    async getExcel(): Promise<void> {
        this.downloadOptions.loading = true;
        try {
            const params = this.getFiltersParamsObject;
            const file = await getEmployeesPersonalRates(params, 'xls');
            downloadExcelFile(file, 'Индивидуальные цены', 'xls');
        } catch (err) {
            showServerError(err, 'Файл не загружен');
        }
        this.downloadOptions.loading = false;
    }
    async copyPrices(): Promise<void>{
        try {
            if (this.affiliate?.master_id == null || this.affiliate?.id == null) {
                return;
            }
            this.downloadOptions.loading = true;
            await cloneEmployeesPersonalRates({
                source_affiliate_id: this.affiliate.master_id,
                target_affiliate_id: this.affiliate.id,
            });
            new NotificationMessage('Индивидуальные цены и доступы к офферам мастера скопированы', 'success');
            const wrapper = this.$refs.tablePersonalRates as InstanceType<typeof TablePageWrapper>;
            await wrapper.getPagination(0);
        } catch (err) {
            showServerError(err, 'Что-то пошло не так');
        }
        this.downloadOptions.loading = false;
    }
}
